/* eslint-disable no-unused-vars */
import React, { useId, useState } from 'react';
import {
  Box,
  IconButton,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { CalendarBlank } from 'phosphor-react';
import { ptBR } from 'date-fns/locale/pt-BR';
import { Calendar } from './Calendar';
import {
  formatIsoDate,
  maskDate,
  parseIsoDate,
  toIsoDate,
  toIsoFromMaskedDate,
} from '../../lib/iso-date';

const yearsBeforeAnchor = 5;
const yearsAfterAnchor = 5;

export interface DateFieldProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
  placeholder?: string;
  monthAnchor?: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  size?: 'sm' | 'md';
  yearsBefore?: number;
  yearsAfter?: number;
  fromDate?: Date;
  toDate?: Date;
  'data-testid'?: string;
}

export const DateField = ({
  value,
  onChange,
  id,
  label,
  placeholder = 'dd/mm/aaaa',
  monthAnchor,
  isInvalid,
  isDisabled,
  size = 'md',
  yearsBefore = yearsBeforeAnchor,
  yearsAfter = yearsAfterAnchor,
  fromDate,
  toDate,
  ...rest
}: DateFieldProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const popoverId = `date-field-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;
  const [draft, setDraft] = useState(() => formatIsoDate(value));
  const [lastValue, setLastValue] = useState(value);

  if (value !== lastValue) {
    setLastValue(value);
    if (toIsoFromMaskedDate(draft) !== value) {
      setDraft(formatIsoDate(value));
    }
  }

  const selected = parseIsoDate(value);
  const defaultMonth = selected ?? parseIsoDate(monthAnchor ?? '');
  const anchorYear = (defaultMonth ?? new Date()).getFullYear();
  const addon = size === 'sm' ? '32px' : '38px';

  return (
    <Box position="relative">
      <Popover
        id={popoverId}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom-start"
        isLazy
        returnFocusOnClose={false}
      >
        <PopoverTrigger>
          <IconButton
            type="button"
            variant="ghost"
            aria-label={label ? `Escolher ${label} no calendário` : 'Abrir calendário'}
            icon={<CalendarBlank size={16} />}
            isDisabled={isDisabled}
            position="absolute"
            top="50%"
            left={size === 'sm' ? '3px' : '4px'}
            transform="translateY(-50%)"
            zIndex={2}
            minW={size === 'sm' ? '26px' : '30px'}
            h={size === 'sm' ? '26px' : '30px'}
            borderRadius="full"
            color="content.subtle"
          />
        </PopoverTrigger>
        <PopoverContent w="auto" p="8px">
          <Calendar
            mode="single"
            locale={ptBR}
            captionLayout="dropdown"
            startMonth={fromDate ?? new Date(anchorYear - yearsBefore, 0)}
            endMonth={toDate ?? new Date(anchorYear + yearsAfter, 11)}
            disabled={[
              ...(fromDate ? [{ before: fromDate }] : []),
              ...(toDate ? [{ after: toDate }] : []),
            ]}
            autoFocus
            {...(defaultMonth ? { defaultMonth } : {})}
            {...(selected ? { selected } : {})}
            onSelect={(date?: Date) => {
              if (date) {
                onChange(toIsoDate(date));
                setDraft(formatIsoDate(toIsoDate(date)));
                onClose();
              }
            }}
          />
        </PopoverContent>
      </Popover>

      <Input
        id={id}
        aria-label={label}
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={draft}
        size={size}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        pl={addon}
        onChange={(event) => {
          const masked = maskDate(event.target.value);
          setDraft(masked);
          onChange(toIsoFromMaskedDate(masked));
        }}
        {...rest}
      />
    </Box>
  );
};
